import * as React from 'react';
import { useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import TravellerForm from '../../TravellerForm';
import { TravellerTypes } from '../types';
import { useTheme } from '../../theme';
import { getTravellerName, MOBILE_MIN_WIDTH, useIsMobile } from '../../utils';
import UserCard from '../../UserCard/components/UserCard';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { LocationSuggestionFragment, TravellerProfile, UserProfileParameters } from '@websky/graphql';
import { useRenderers } from '../../renderProps';
import { CdnIcon } from '@shared/ui';
import { useToggleable } from '../../hooks';
import { ModalX, Header, IconButton } from '../../BaseComponents';
import { TravellerType } from '../../TravellerForm/types';

interface Props {
	traveller?: TravellerType;
	cities?: LocationSuggestionFragment[];
	type?: TravellerTypes;
	onSubmit: (values: UserProfileParameters) => Promise<boolean>;
	onDelete?: (id: string) => Promise<boolean>;
	expanded?: boolean;
	handleChange?: (expanded: boolean) => void;
	redirectToPassengersPage?: boolean;
	disabled?: boolean;
}

const Traveller: React.FunctionComponent<Props> = ({
	type,
	traveller,
	cities,
	onSubmit,
	onDelete,
	expanded,
	handleChange,
	redirectToPassengersPage,
	disabled
}) => {
	const { t } = useTranslation('Account');
	const css = useTheme('Traveller').Traveller;

	const isMobile = useIsMobile();

	const { isOpen, close, toggle, setOpen } = useToggleable(!!redirectToPassengersPage || !!expanded);

	const { TravellerForm: TravellerFormOverride = TravellerForm } = useRenderers();

	const onChange = (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
		setOpen(isExpanded);
	};

	useEffect(() => {
		if (expanded !== isOpen) {
			setOpen(expanded);
		}
	}, [expanded]);

	useEffect(() => {
		if (expanded !== isOpen && handleChange) {
			handleChange(isOpen);
		}
	}, [isOpen]);

	const userLabel = React.useMemo(() => {
		if (type === TravellerTypes.CompanionEmpty) {
			return t('Add companion');
		}

		return getTravellerName(traveller);
	}, [type, traveller]);

	const travellerFormProps: React.ComponentProps<typeof TravellerForm> = {
		type,
		traveller: { ...traveller, documents: traveller?.id !== '0' ? traveller.documents : [] },
		cities,
		onSubmit: values => onSubmit({ ...values }),
		onDelete: () => onDelete(traveller.id)
	};

	return (
		<>
			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<Accordion
					className={css.wrapper}
					classes={{ root: css.accordion__root, expanded: css.expanded }}
					expanded={isOpen}
					onChange={onChange}
				>
					<AccordionSummary
						className={cn(
							{ [css.header__redirect]: redirectToPassengersPage },
							{ [css.disabled]: disabled },
							css.header
						)}
						classes={{ content: css.accordionSummary__content }}
						expandIcon={
							<div className={css.expandIcon}>
								<CdnIcon className={css.expandIcon} icon="Arrow/Expand_more" />
							</div>
						}
					>
						<UserCard
							profile={traveller as TravellerProfile}
							editText={type === TravellerTypes.PersonalDetails ? t('Edit') : null}
							inline={true}
							type={type}
							className={css.userCard}
							headerClassName={css.summary}
						/>
					</AccordionSummary>

					{!redirectToPassengersPage && (
						<AccordionDetails className={css.content__traveller}>
							<TravellerFormOverride {...travellerFormProps} onClose={close} />
						</AccordionDetails>
					)}
				</Accordion>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				<UserCard
					profile={traveller as TravellerProfile}
					type={type}
					className={css.userCard}
					headerClassName={cn({ [css.disabled]: disabled }, css.summary)}
					onClick={toggle}
				/>

				<ModalX
					classes={{
						paper: css.popup__paper,
						top_wrapper: css.popup__top_wrapper
					}}
					size="fullscreen"
					title={
						<Header
							className={css.popup__header}
							classes={{
								inner: css.popup__inner,
								leftSlot: css.popup__leftSlot,
								centerSlot: css.popup__centerSlot
							}}
							left={
								<IconButton className={css.popup__button} onClick={close}>
									<CdnIcon icon="Arrow/Back" />
								</IconButton>
							}
							center={<span className={css.popup__title}>{userLabel}</span>}
							equalSlotsBasis={false}
							showEmptySlots={false}
						/>
					}
					isOpen={!disabled && isOpen}
					onClose={close}
					ignoreClose={isMobile}
					noPadding
				>
					<TravellerFormOverride key={`${isOpen}`} {...travellerFormProps} onClose={close} />
				</ModalX>
			</MediaQuery>
		</>
	);
};

export default Traveller;
